import { z } from 'zod';
import { Api } from '@automapa/svelte-automapa-template';
import {
    JobCountSchema,
    JobRequestSchema,
    JobResponseSchema,
    JobWithRealisationResponseSchema,
    JobWithColumnAssignmentsResponseSchema,
    JobWithRealisationAndRecentRejectionResponseSchema,
} from './types';
import type { JobRequestType } from './types';
import type { JobCountType } from './types';
import type {
    JobResponseType,
    JobWithRealisationResponseType,
    JobWithColumnAssignmentsResponseType,
    JobWithRealisationAndRecentRejectionResponseType,
} from './types';
import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
import { type QueriesType, QueriesTypeSchema } from '../telematics/types';
import {
    AutoSuggesterRequestSchema,
    type AutoSuggesterRequestType,
    AutoSuggesterResponseSchema,
    type AutoSuggesterResponseType,
} from '../telematics/components/FilterByParameters/featureAutoSuggester';
import { get } from 'svelte/store';
import { api } from '../../../stores/api';
import { planning } from '../../../stores/store';
import { FriendlyDateGenerator } from '../../utilities/FriendlyDateGenerator';
import { ScenarioApi } from '../Planning/ScenarioApi';

export const AsyncImportResponseSchema = z.object({ import_id: z.number() });
export type AsyncImportResponseType = z.infer<typeof AsyncImportResponseSchema>;

export const AsyncImportResultSchema = z.object({
    done: z.boolean(),
    jobs_total: z.number().optional(),
    jobs_geocoded: z.number().optional(),
    jobs: z.array(z.any()).optional(),
});
export type AsyncImportResultType = z.infer<typeof AsyncImportResultSchema>;

// FIXME: move it to types.ts or somewhere else
export const ModifyValuesSchema = z.record(z.string(), z.any());
export type ModifyValuesType = z.infer<typeof ModifyValuesSchema>;

export const ModifyRequestSchema = z.object({
    query: QueriesTypeSchema,
    values: ModifyValuesSchema,
});
export type ModifyRequestType = z.infer<typeof ModifyRequestSchema>;

export const ModifyResponseSchema = z.object({
    modified: z.number(),
});
export type ModifyResponseType = z.infer<typeof ModifyResponseSchema>;

export const queryAdhoc = {
    // process_status: { $in: [0,1]},
};

export const queryOverdue = {
    // process_status: { $in: [0,1]},
    target_date: { $lt: FriendlyDateGenerator.getTodayYYYYMMDD() },
};

export const getQueryAdhoc = (selectedDay: string, planning_started: string) => {
    return {
        created: { $gt: planning_started },
        target_date: selectedDay,
    };
};

export const JobHistoryEventResponseSchema = z.object({
    event_id: z.number(),
    event_type: z.string(),
    event_description: z.string().catch(''),
    event_time: z.string(),
    user_id: z.number().nullable().catch(null),
    user_email: z.string().nullable().catch(null),
    diff_data: z.any(),
    extra_data: z.any(),
});
export type JobHistoryEventResponseType = z.infer<typeof JobHistoryEventResponseSchema>;

export class JobApi {
    public static async getWithQueries(
        api: Api,
        queries: QueriesType,
        filter: ApiFilterBuilder
    ): Promise<JobResponseType[]> {
        return api.getData<QueriesType, JobResponseType[]>(
            'post',
            '/job/query' + filter.getUri(),
            queries,
            QueriesTypeSchema,
            z.array(JobResponseSchema)
        );
    }

    public static async getCountWithQueries(
        api: Api,
        queries: QueriesType,
        filter: ApiFilterBuilder
    ): Promise<JobCountType> {
        return api.getData<QueriesType, JobCountType>(
            'post',
            '/job/count/query' + filter.getUri(),
            queries,
            QueriesTypeSchema,
            JobCountSchema
        );
    }

    public static async getWithRealisationByQueries(
        api: Api,
        queries: QueriesType,
        filter: ApiFilterBuilder
    ): Promise<JobResponseType[]> {
        let tempFilter = new ApiFilterBuilder(-1);
        for (let prop in tempFilter) {
            tempFilter[prop] = structuredClone(filter[prop]);
        }
        tempFilter.setRelations(['assignment_job']);
        tempFilter.setProjectionFields(['job.*', 'geom', 'assignment_job>details@realisation']);
        return api.getData<QueriesType, JobResponseType[]>(
            'post',
            '/job/query' + tempFilter.getUri(),
            queries,
            QueriesTypeSchema,
            z.array(JobWithRealisationResponseSchema)
        );
    }

    public static async getWithRealisationAndRecentRejectionByQueries(
        api: Api,
        queries: QueriesType,
        filter: ApiFilterBuilder
    ): Promise<JobResponseType[]> {
        let tempFilter = new ApiFilterBuilder(-1);
        for (let prop in tempFilter) {
            tempFilter[prop] = structuredClone(filter[prop]);
        }
        tempFilter.setRelations(['assignment_job', 'assignment_job_rejection']);
        tempFilter.setProjectionFields([
            'job.*',
            'geom',
            'assignment_job>details@realisation',
            'assignment_job_rejection>driver_info@rejection',
        ]);
        let tempQueries = {
            $and: [
                queries,
                {
                    $or: [
                        { rejected: false },
                        {
                            'rejected_assignments.-1::int': {
                                $column: 'assignment_job_rejection.assignment_id',
                            },
                        },
                    ],
                },
            ],
        };
        return api.getData<QueriesType, JobResponseType[]>(
            'post',
            '/job/query' + tempFilter.getUri(),
            tempQueries,
            QueriesTypeSchema,
            z.array(JobWithRealisationAndRecentRejectionResponseSchema)
        );
    }

    public static async modify(
        api: Api,
        query: QueriesType,
        values: ModifyValuesType
    ): Promise<ModifyResponseType> {
        return api.getData<ModifyRequestType, ModifyResponseType>(
            'post',
            '/job/modify',
            { query, values },
            ModifyRequestSchema,
            ModifyResponseSchema
        );
    }

    public static async updateTargetDate(
        api: Api,
        id: number,
        targetDate: string
    ): Promise<JobResponseType> {
        return api.patch<JobResponseType>(
            '/job/' + id,
            'replace',
            '/target_date',
            targetDate,
            JobResponseSchema
        );
    }

    // public static async getJobsCount(api: Api, filter: ApiFilterBuilder): Promise<JobCountType> {
    //     return api.getData<undefined, JobCountType>(
    //         'get',
    //         '/job/count' + filter.getUri(),
    //         undefined,
    //         z.undefined(),
    //         JobCountSchema
    //     );
    // }

    public static async getOne(api: Api, jobId: number): Promise<JobResponseType> {
        return api.getData<undefined, JobResponseType>(
            'get',
            `/job/${jobId}`,
            undefined,
            z.undefined(),
            JobResponseSchema
        );
    }

    public static async getOneWithRealisation(
        api: Api,
        jobId: number
    ): Promise<JobWithRealisationResponseType> {
        let filter = new ApiFilterBuilder(-1);
        filter.setRelations(['assignment_job']);
        filter.setProjectionFields(['job.*', 'geom', 'assignment_job>details@realisation']);
        return api.getData<undefined, JobWithRealisationResponseType>(
            'get',
            `/job/${jobId}` + filter.getUri(),
            undefined,
            z.undefined(),
            JobWithRealisationResponseSchema
        );
    }

    public static async getOneWithColumnAssignments(
        api: Api,
        jobId: number
    ): Promise<JobWithRealisationResponseType> {
        let filter = new ApiFilterBuilder(-1);
        filter.setRelations(['import']);
        filter.setProjectionFields(['job.*', 'import.columns_assignment']);
        return api.getData<undefined, JobWithColumnAssignmentsResponseType>(
            'get',
            `/job/${jobId}` + filter.getUri(),
            undefined,
            z.undefined(),
            JobWithColumnAssignmentsResponseSchema
        );
    }

    public static async getOneWithRealisationAndRecentRejection(
        api: Api,
        jobId: number
    ): Promise<JobWithRealisationAndRecentRejectionResponseType> {
        let filter = new ApiFilterBuilder(-1);
        filter.setRelations(['assignment_job', 'assignment_job_rejection']);
        filter.setProjectionFields([
            'job.*',
            'geom',
            'assignment_job>details@realisation',
            'assignment_job_rejection>driver_info@rejection',
        ]);
        let queries = {
            'job.id': jobId,
            $or: [
                { rejected: false },
                {
                    'rejected_assignments.-1::int': {
                        $column: 'assignment_job_rejection.assignment_id',
                    },
                },
            ],
        };
        return new Promise((resolve, reject) => {
            api.getData<QueriesType, JobResponseType[]>(
                'post',
                '/job/query' + filter.getUri(),
                queries,
                QueriesTypeSchema,
                z.array(JobWithRealisationAndRecentRejectionResponseSchema)
            )
                .then(response => {
                    resolve(response[0] ?? null);
                })
                .catch(() => reject());
        });
    }

    public static async updateOne(
        api: Api,
        jobId: number,
        job: JobRequestType
    ): Promise<JobResponseType> {
        return api.getData<JobRequestType, JobResponseType>(
            'put',
            `/job/${jobId}`,
            job,
            JobRequestSchema,
            JobResponseSchema
        );
    }

    public static async geocode(api: Api, jobId: number): Promise<JobResponseType> {
        return api.getData<{}, JobResponseType>(
            'post',
            `/job/${jobId}/geocode`,
            {},
            z.object({}),
            JobResponseSchema
        );
    }

    public static async getByRouteId(api: Api, routeId: number): Promise<JobResponseType[]> {
        return api.getData<undefined, JobResponseType[]>(
            'get',
            `/job/${routeId}/jobs`,
            undefined,
            z.undefined(),
            z.array(JobResponseSchema)
        );
    }

    public static async filter(api: Api, filter: ApiFilterBuilder): Promise<JobResponseType[]> {
        return api.getData<undefined, JobResponseType[]>(
            'get',
            `/jobs` + filter.getUri(),
            undefined,
            z.undefined(),
            z.array(JobResponseSchema)
        );
    }

    public static async removeAll(api: Api) {
        return this.removeWithQuery(api, { $and: [{}, { id: { $nin: [] } }] });
    }

    public static async removeOne(api: Api, id: number) {
        return api.delete(`/job/${id}`);
    }

    public static async removeWithQuery(api: Api, query: QueriesType) {
        return api.getData<QueriesType, { removed: number }>(
            'post',
            `/job/remove`,
            query,
            QueriesTypeSchema,
            z.object({ removed: z.number() })
        );
    }

    public static async getProcessStatus(api: Api, id: number) {
        // GET https://lastmile-test.automapa.pl/api/job/1138?projection=process_status
        return api.getData<undefined, { process_status: number }>(
            'get',
            `/job/${id}?projection=process_status`,
            undefined,
            z.undefined(),
            z.object({ process_status: z.number().catch(-1) })
        );
    }

    //     //         fetch('./api/jobs/suggest?' + featureAutoSuggester.convertRequestToURLSearchParams(request), {
    //     //             method: 'GET',
    public static async getSuggestions(api: Api, uri: string): Promise<AutoSuggesterResponseType> {
        return api.getData<undefined, AutoSuggesterResponseType>(
            'get',
            '/job/suggest?' + uri,
            undefined,
            z.any(),
            AutoSuggesterResponseSchema
        );
    }

    public static async createOne(api: Api, job: JobRequestType): Promise<JobResponseType> {
        return api.getData<JobRequestType, JobResponseType>(
            'post',
            '/job',
            job,
            JobRequestSchema,
            JobResponseSchema
        );
    }

    public static async setCompleted(api: Api, id: number): Promise<JobResponseType> {
        let dateYYYYMMDD_HHMMSS = new Date().toISOString().replace('T', ' ').replace('Z', '');
        // remove last 3 digits of seconds 2024-02-28 13:33:13.535
        dateYYYYMMDD_HHMMSS = dateYYYYMMDD_HHMMSS.substring(0, dateYYYYMMDD_HHMMSS.length - 4);
        await Promise.all([
            // api.patch<JobResponseType>('/job/'+id, 'replace', '/process_status', 4, JobResponseSchema),
            api.patch<JobResponseType>(
                '/job/' + id,
                'replace',
                '/completed',
                dateYYYYMMDD_HHMMSS,
                JobResponseSchema
            ),
        ]);
        return this.getOne(api, id);
    }

    public static async getCountAdhoc(api: Api, selectedDay: string): Promise<JobCountType> {
        let scenario = await ScenarioApi.getOneForDay(api, selectedDay);
        if (typeof scenario !== 'undefined' && scenario?.planning_started !== null) {
            return this.getCountWithQueries(
                api,
                getQueryAdhoc(selectedDay, scenario.planning_started),
                new ApiFilterBuilder(1)
            );
        }
        if (scenario === null) {
            return Promise.resolve({ count: -2 });
        }
        if (scenario?.planning_started === null) {
            return Promise.resolve({ count: -1 });
        }
        return Promise.resolve({ count: -3 });
    }

    public static async getAdhoc(api: Api, selectedDay: string): Promise<JobResponseType[]> {
        let scenario = await ScenarioApi.getOneForDay(api, selectedDay);
        if (scenario === null) {
            return Promise.resolve([]);
        }
        if (scenario.planning_started === null) {
            return Promise.resolve([]);
        }
        return this.getWithQueries(
            api,
            getQueryAdhoc(selectedDay, scenario.planning_started),
            new ApiFilterBuilder(1000)
        );
    }

    public static async getCountOverdue(api: Api): Promise<JobCountType> {
        return this.getCountWithQueries(api, queryOverdue, new ApiFilterBuilder(1));
    }

    public static async getOverdue(api: Api): Promise<JobResponseType[]> {
        return this.getWithQueries(api, queryOverdue, new ApiFilterBuilder(1000));
    }

    public static async revgeo(api: Api, x: number, y: number): Promise<any> {
        return api.getData<undefined, any>(
            'get',
            '/job/revgeo?x=' + x + '&y=' + y,
            undefined,
            z.any(),
            z.any()
        );
    }

    public static async setAdhocStatus(
        api: Api,
        jobId: number,
        status: boolean
    ): Promise<ModifyResponseType> {
        return this.modify(api, { id: jobId }, { ad_hoc: status });
    }

    // https://er.automapa.pl/issues/40585
    // POST https://<host>/api/jobs/1578473/history/ {}
    public static async getHistory(api: Api, jobId: number) {
        return api.getData<any, JobHistoryEventResponseType[]>(
            'post',
            `/job/${jobId}/history`,
            {},
            z.object({}),
            z.array(JobHistoryEventResponseSchema)
        );
    }

    public static async getDataForNewJob(api: Api): Promise<any> {
        return api.getData<undefined, AutoSuggesterResponseType>(
            'get',
            '/job/get_data_for_new_job',
            undefined,
            z.any(),
            z.any()
        );
    }

    public static async asyncImport(
        api: Api,
        payload: Record<string, unknown>
    ): Promise<AsyncImportResponseType> {
        return api.getData(
            'post',
            '/job/import',
            payload,
            z.record(z.unknown()),
            AsyncImportResponseSchema
        );
    }

    public static async asyncImportResult(
        api: Api,
        importId: number
    ): Promise<AsyncImportResultType> {
        return api.getData(
            'post',
            '/job/import_result',
            { import_id: importId },
            z.object({ import_id: z.number() }),
            AsyncImportResultSchema
        );
    }
}
